import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import TagAlongDialog, { daysLeft } from "@/components/TagAlongDialog";

const OUTCOMES = [
  { v: "", label: "All outcomes" },
  { v: "invested", label: "Invested €20K" },
  { v: "passed", label: "Passed" },
  { v: "follow_up", label: "Follow-up" },
];

const SORTS = [
  { v: "newest", label: "Newest" },
  { v: "most_watched", label: "Most watched" },
  { v: "invested_only", label: "Invested only" },
];

function OutcomeBadge({ outcome }) {
  const map = {
    invested: { label: "Invested €20K", cls: "bg-success text-black" },
    passed: { label: "Passed", cls: "bg-danger text-white" },
    follow_up: { label: "Follow-up", cls: "bg-warn text-black" },
  };
  const cfg = map[outcome] || { label: outcome, cls: "bg-white/10 text-white" };
  return (
    <span data-testid={`outcome-${outcome}`} className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest font-bold ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

export default function Episodes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ outcome: "", industry: "", country: "", year: "", q: "", sort: "newest" });
  const [meta, setMeta] = useState({ industries: [], countries: [], years: [] });

  useEffect(() => {
    api.get("/episodes/filters").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/episodes", { params }).then(({ data }) => setItems(data)).finally(() => setLoading(false));
  }, [filters]);

  const setF = (k, v) => setFilters((s) => ({ ...s, [k]: v === "__all__" ? "" : v }));

  return (
    <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16" data-testid="episodes-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-4">Library</div>
          <h1 className="font-heading text-5xl md:text-7xl font-black uppercase tracking-tighter">Every episode.<br /><span className="text-white/50">On the record.</span></h1>
        </div>
        <div className="text-white/50 max-w-md">
          Watch what €20,000 looks like on the line. Every deal, every rejection, every follow-up — all publicly witnessed.
        </div>
      </div>

      {/* FILTERS */}
      <div className="grid md:grid-cols-6 gap-3 border border-white/10 p-4 bg-surface mb-10" data-testid="filters-bar">
        <div className="md:col-span-2">
          <Input
            data-testid="search-input"
            placeholder="Search startup or keyword…"
            value={filters.q}
            onChange={(e) => setF("q", e.target.value)}
            className="bg-black border-white/10 text-white placeholder:text-white/40 rounded-none focus-visible:ring-lime"
          />
        </div>
        <Select value={filters.outcome || "__all__"} onValueChange={(v) => setF("outcome", v)}>
          <SelectTrigger data-testid="filter-outcome" className="bg-black border-white/10 rounded-none text-white"><SelectValue placeholder="Outcome" /></SelectTrigger>
          <SelectContent className="bg-surface border-white/10 rounded-none text-white">
            {OUTCOMES.map((o) => <SelectItem key={o.v || "all"} value={o.v || "__all__"}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.industry || "__all__"} onValueChange={(v) => setF("industry", v)}>
          <SelectTrigger data-testid="filter-industry" className="bg-black border-white/10 rounded-none text-white"><SelectValue placeholder="Industry" /></SelectTrigger>
          <SelectContent className="bg-surface border-white/10 rounded-none text-white">
            <SelectItem value="__all__">All industries</SelectItem>
            {meta.industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.country || "__all__"} onValueChange={(v) => setF("country", v)}>
          <SelectTrigger data-testid="filter-country" className="bg-black border-white/10 rounded-none text-white"><SelectValue placeholder="Country" /></SelectTrigger>
          <SelectContent className="bg-surface border-white/10 rounded-none text-white">
            <SelectItem value="__all__">All countries</SelectItem>
            {meta.countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.sort} onValueChange={(v) => setF("sort", v)}>
          <SelectTrigger data-testid="filter-sort" className="bg-black border-white/10 rounded-none text-white"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent className="bg-surface border-white/10 rounded-none text-white">
            {SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* GRID */}
      {loading ? (
        <div className="text-white/50 font-mono text-sm uppercase tracking-widest py-24 text-center">Loading episodes…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-white/10 p-16 text-center" data-testid="empty-state">
          <div className="font-heading text-3xl md:text-4xl font-black uppercase mb-3">No episodes yet</div>
          <p className="text-white/50 mb-6">Once the show goes live, every episode lands here.</p>
          <Link to="/apply" className="inline-block px-6 py-3 bg-lime text-black font-heading font-bold uppercase tracking-wider hover:scale-[1.03] transition-transform">
            Apply to Pitch →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="episodes-grid">
          {items.map((ep) => {
            const isInvested = ep.outcome === "invested";
            const taEnabled = ep.tag_along_enabled !== false;
            const soloEnabled = ep.solo_invest_enabled === true && !isInvested;
            const left = daysLeft(ep.episode_date);
            const total = Number(ep.tag_along_total || 0);
            return (
              <div
                key={ep.id}
                data-testid={`episode-card-${ep.slug}`}
                className="group bg-surface-elevated border border-white/10 hover:border-lime transition-colors flex flex-col"
              >
                <Link to={`/episodes/${ep.slug}`} className="block">
                  <div className="aspect-video relative overflow-hidden bg-black">
                    {ep.cover_image_path ? (
                      <img src={fileUrl(ep.cover_image_path)} alt={ep.startup_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/20 font-heading text-4xl font-black uppercase">
                        {ep.startup_name?.slice(0, 3)}
                      </div>
                    )}
                    <div className="absolute top-3 left-3"><OutcomeBadge outcome={ep.outcome} /></div>
                  </div>
                </Link>
                <div className="p-5 flex-1 flex flex-col">
                  <Link to={`/episodes/${ep.slug}`}>
                    <div className="font-heading text-xl font-black uppercase tracking-tight text-white group-hover:text-lime transition-colors">{ep.startup_name}</div>
                    <div className="mt-2 text-white/60 text-sm line-clamp-2">{ep.one_liner}</div>
                  </Link>
                  <div className="mt-4 flex items-center justify-between text-xs font-mono uppercase tracking-widest text-white/40">
                    <span>{ep.industry || "—"}</span>
                    <span>{ep.country || "—"}</span>
                  </div>
                  {isInvested && taEnabled && (
                    <div className="mt-4 pt-4 border-t border-white/10" data-testid={`card-tagalong-${ep.slug}`}>
                      <div className="flex items-baseline justify-between text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">
                        <span>Tagged along</span>
                        <span className="text-violet font-bold text-sm tabular-nums">€{total.toLocaleString()}</span>
                      </div>
                      {left > 0 ? (
                        <>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">
                            Closes in <span className="text-white tabular-nums">{left}</span> {left === 1 ? "day" : "days"}
                          </div>
                          <TagAlongDialog
                            episode={ep}
                            triggerTestId={`card-tagalong-btn-${ep.slug}`}
                            trigger={(open) => (
                              <button
                                data-testid={`card-tagalong-btn-${ep.slug}`}
                                onClick={(e) => { e.preventDefault(); open(); }}
                                className="w-full py-2.5 bg-lime text-black font-heading font-black text-xs uppercase tracking-widest hover:scale-[1.02] transition-transform"
                              >
                                Tag along →
                              </button>
                            )}
                          />
                        </>
                      ) : (
                        <div data-testid={`card-tagalong-closed-${ep.slug}`} className="w-full py-2.5 bg-white/5 text-white/50 font-mono text-[10px] uppercase tracking-widest text-center">
                          Opportunity closed
                        </div>
                      )}
                    </div>
                  )}
                  {soloEnabled && (
                    <div className="mt-4 pt-4 border-t border-white/10" data-testid={`card-solo-${ep.slug}`}>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">
                        Simone passed — you don't have to
                      </div>
                      <TagAlongDialog
                        episode={ep}
                        mode="solo_invest"
                        triggerTestId={`card-solo-btn-${ep.slug}`}
                        trigger={(open) => (
                          <button
                            data-testid={`card-solo-btn-${ep.slug}`}
                            onClick={(e) => { e.preventDefault(); open(); }}
                            className="w-full py-2.5 bg-surface border border-lime text-lime font-heading font-black text-xs uppercase tracking-widest hover:bg-lime hover:text-black transition-colors"
                          >
                            Invest anyway →
                          </button>
                        )}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
